import {productGroupService} from "@/services/ProductGroupService";
import {imageService} from "@/services/ImageService";
import {toast} from "vue3-toastify";
import {i18n} from "./../lang";
import {productGroupIdOf} from "@/helpers/catalog";

const state = {
    productGroups: [],
    isAddingGroup: false,
    addingParentGroupId: null,
    editingGroup: null,
    deletingGroup: null,
    editGroupRequest : {
        loading: false
    },
    addGroupRequest : {
        loading: false
    },
    getGroupsRequest : {
        loading: false
    },
    deleteGroupRequest: {
        loading: false
    },
    groupsFetchedAt: null,
    groupsWithProduct: false,
    groupsStale: false
}

const groupsRequests = {};
let groupsWrites = 0;

function blockingGroupNames(groupIds, state, rootState) {
    const products = rootState?.product?.products || [];
    const groupsWithProducts = new Set(products.map(product => String(productGroupIdOf(product))));
    const deleting = new Set(groupIds.map(id => String(id)));
    const hasKeptSubgroup = groupId => state.productGroups.some(group =>
        String(group.parentGroupId) === groupId && !deleting.has(String(group.productGroupId)));
    return groupIds
        .map(id => state.productGroups.find(group => String(group.productGroupId) === String(id)))
        .filter(group => group && (groupsWithProducts.has(String(group.productGroupId)) || hasKeptSubgroup(String(group.productGroupId))))
        .map(group => group.name);
}

function rejectBlockedDeletion(commit, names) {
    toast.error(i18n.global.t("productGroupPage.deleteBlocked", {groups: names.join(", ")}));
    commit('setDeletingGroup', null);
    commit('setDeleteGroupRequest', {loading: false});
}

const actions = {
    ensureProductGroups({state, dispatch}, withProduct = false){
        const upToDate = state.productGroups.length && !state.groupsStale && state.groupsWithProduct === withProduct;
        if (upToDate) return Promise.resolve(state.productGroups);
        return dispatch('refreshProductGroups', withProduct);
    },
    refreshProductGroups({dispatch}, withProduct = false){
        const key = withProduct ? 'withProduct' : 'plain';
        if (!groupsRequests[key]) {
            groupsRequests[key] = dispatch('getProductGroups', withProduct).finally(() => {
                delete groupsRequests[key];
            });
        }
        return groupsRequests[key];
    },
    getProductGroups({commit, dispatch}, withProduct = false){
        const writesAtStart = groupsWrites;
        commit('setGetGroupsRequest', {loading: true})
        return productGroupService.getProductGroups(withProduct)
            .then((response) => {
                if (groupsWrites !== writesAtStart) return dispatch('getProductGroups', withProduct);
                const data = response.data.map(group => ({
                    ...group,
                    checked: false
                }));
                commit('setGetGroupsRequest', {loading: false})
                commit('updateProductGroups', data);
                commit('setGroupsFetchedAt', {at: Date.now(), withProduct});
                return data;
            })
            .catch((error) => commit('setGetGroupsRequest', {loading: false, message: error}))
    },
    async updateProductGroup({commit, state}, {productGroup, file, orgId}){
        commit('setEditGroupRequest', {loading: true});
        if (file){
            try {
                const imageUrl = await imageService.uploadImage(file, orgId);
                productGroup.imageUrl = imageUrl;
            } catch (error) {
                console.error("Error uploading image : "+error)
                commit('setEditGroupRequest', {loading: false, message: error})
                return false;
            }
        }
        return productGroupService.updateProductGroup(productGroup)
            .then((response) => {
                toast.success(i18n.global.t("toast.successEdit"));
                const updatedGroups = state.productGroups.map(x => (x.productGroupId === productGroup.productGroupId ? productGroup : x));
                commit('setEditGroupRequest', {loading: false});
                commit('updateProductGroups', updatedGroups);
                commit('product/syncProductGroups', [productGroup], {root: true});
                commit('setEditingGroup', false);
                return true;
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.errorEdit"));
                commit('setEditGroupRequest', {loading: false, message: error})
                return false;
            })
    },
    async addProductGroup({ commit, state }, { productGroup, file, orgId }) {
        commit('setAddGroupsRequest', { loading: true });

        try {
            if (file) {
                const imageUrl = await imageService.uploadImage(file, orgId);
                productGroup.imageUrl = imageUrl;
            }

            const response = await productGroupService.addProductGroup(productGroup);

            toast.success(i18n.global.t("toast.createSuccess"));

            const updatedGroups = [...state.productGroups, response.data];
            commit('updateProductGroups', updatedGroups);
            commit('setAddGroupsRequest', { loading: false });
            commit('setIsAddingGroup', false);

            return response.data;
        } catch (error) {
            toast.error(i18n.global.t("toast.createError"));
            commit('setAddGroupsRequest', { loading: false, message: error });
        }
    },
    deleteProductGroup({commit, state, rootState}, productGroup){
        const blocked = blockingGroupNames([productGroup.productGroupId], state, rootState);
        if (blocked.length) {
            rejectBlockedDeletion(commit, blocked);
            return Promise.resolve(false);
        }
        return productGroupService.deleteProductGroup(productGroup)
            .then((response) => {
                toast.success(i18n.global.t("toast.deleteSuccess"));
                const updatedGroups = state.productGroups.filter(function(element) {
                    return element.productGroupId !== productGroup.productGroupId;
                });
                commit('updateProductGroups', updatedGroups);
                commit('setDeletingGroup', null);
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.deleteError"));
                commit('setDeleteGroupRequest', {loading: false, message: error})
            });
    },
    deleteProductGroupBatch({commit, state, rootState}, productGroupIds){
        const ids = Array.isArray(productGroupIds)
            ? productGroupIds.map(group => (typeof group === 'object' ? group.productGroupId : group))
            : [productGroupIds].filter(Boolean);

        const cleanIds = ids
            .map(id => Number(id))
            .filter(id => !isNaN(id));

        const blocked = blockingGroupNames(cleanIds, state, rootState);
        if (blocked.length) {
            rejectBlockedDeletion(commit, blocked);
            return Promise.resolve(false);
        }

        commit('setDeleteGroupRequest', {loading: true})
        return productGroupService.deleteProductGroupBatch(cleanIds)
            .then((response) => {
                toast.success(i18n.global.t("toast.deleteSuccess"));
                const idsSet = new Set(cleanIds.map(id => String(id)));
                const updatedGroups = state.productGroups.filter(
                    element => !idsSet.has(String(element.productGroupId))
                );
                commit('updateProductGroups', updatedGroups);
                commit('setDeletingGroup', null);
                commit('setDeleteGroupRequest', {loading: false})
            })
            .catch((error) => {
                console.log(error)
                toast.error(error);
                commit('setDeleteGroupRequest', {loading: false, message: error})
            });
    },
    async duplicateProductGroup({commit, state}, productGroup){
        commit('setEditGroupRequest', {loading: true});
        const newGroup = {
            ...productGroup,
            name: productGroup.name + " copy"
        };
        try {
            const response = await productGroupService.addProductGroup(newGroup);
            const createdGroup = {
                ...response.data,
                checked: false,
                products: response.data?.products || []
            };

            toast.success(i18n.global.t("toast.duplicateSuccess"));
            commit('setEditGroupRequest', {loading: false});
            const updatedGroups = [...state.productGroups];
            const duplicatedIndex = updatedGroups.findIndex(
                group => String(group.productGroupId) === String(productGroup.productGroupId)
            );

            if (duplicatedIndex >= 0) {
                updatedGroups.splice(duplicatedIndex + 1, 0, createdGroup);
            } else {
                updatedGroups.push(createdGroup);
            }

            commit("updateProductGroups", updatedGroups);

            return createdGroup;
        } catch (error) {
            console.error("Error deleting : "+error)
            toast.error(i18n.global.t("toast.duplicateError"));
            commit('setEditGroupRequest', {loading: false, message: error})
            return null;
        }
    },
    async changeSpecificField({commit, dispatch}, {productGroup, field, file, orgId}) {
        const touched = ['userHidden', 'webHidden', 'kioskHidden', 'kitchenPrintEnabled', 'imageUrl'];
        const previous = Object.fromEntries(touched.map(key => [key, productGroup[key]]));
        const restore = () => touched.forEach(key => { if (productGroup[key] !== previous[key]) productGroup[key] = previous[key]; });
        try {
            if (file) {
                const imageUrl = await imageService.uploadImage(file, orgId || productGroup.orgId);
                productGroup.imageUrl = imageUrl;
            }
            if (field === 'userHidden') productGroup.userHidden = !productGroup.userHidden;
            if (field === 'webHidden') productGroup.webHidden = !productGroup.webHidden;
            if (field === 'kioskHidden') productGroup.kioskHidden = !productGroup.kioskHidden;
            if (field === 'kitchenPrintEnabled') productGroup.kitchenPrintEnabled = !productGroup.kitchenPrintEnabled;
            const saved = await dispatch('updateProductGroup', { productGroup, file: null, orgId: productGroup.orgId || orgId });
            if (!saved) restore();
            return saved;
        } catch (error) {
            console.error(error);
            restore();
            commit('setEditGroupRequest', {loading: false, message: error});
            return false;
        }
    },
    async updateProductGroupBatch({commit, state}, updated, showToast= true) {
        commit('setEditGroupRequest', {loading: true});
        return productGroupService.updateProductsGroupBatch(updated)
            .then(() => {
                if (showToast){
                    toast.success(i18n.global.t("toast.successEdit"));
                }
                const arr = Array.isArray(updated) ? updated : [];
                const updatedMap = new Map(arr.map(group => [String(group.productGroupId), group]));

                const newGroups = state.productGroups.map(p => {
                    const key = String(p.productGroupId);
                    return updatedMap.has(key) ? updatedMap.get(key) : p;
                });

                commit('updateProductGroups', newGroups)
                commit('product/syncProductGroups', arr, {root: true});
                commit('setEditGroupRequest', {loading: false});
                return true;
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.errorEdit"));
                commit('setEditGroupRequest', {loading: false, message: error})
                return false;
            })
    }
}

const mutations = {
    setEditingGroup(state, editingGroup){
        state.editingGroup = editingGroup;
        state.isAddingGroup = false;
        state.addingParentGroupId = null;
        state.editGroupRequest = {loading: false, message: null}

    },
    setDeletingGroup(state, deletingGroup){
      state.deletingGroup = deletingGroup;
    },
    setIsAddingGroup(state, isAddingGroup){
        state.isAddingGroup = isAddingGroup;
        state.editingGroup = null;
        state.addingParentGroupId = null;
        state.addGroupRequest = {loading: false, message: null}
    },
    // Ouvre le dialog d'ajout en présélectionnant le groupe parent (création d'un sous-groupe).
    startAddingSubGroup(state, parentId){
        state.isAddingGroup = true;
        state.editingGroup = null;
        state.addingParentGroupId = parentId;
        state.addGroupRequest = {loading: false, message: null}
    },
    setGroupsFetchedAt(state, {at, withProduct}){
        state.groupsFetchedAt = at;
        state.groupsWithProduct = withProduct;
        state.groupsStale = false;
    },
    invalidateProductGroups(state){
        state.groupsStale = true;
    },
    resetProductGroups(state){
        state.productGroups = [];
        state.groupsFetchedAt = null;
        state.groupsWithProduct = false;
        state.groupsStale = false;
    },
    setGetGroupsRequest(state, {loading, message}){
        state.getGroupsRequest = {
            loading: loading,
            message: message
        }
    },
    setAddGroupsRequest(state, {loading, message}){
        state.addGroupRequest = {
            loading: loading,
            message: message
        }
    },
    setEditGroupRequest(state, {loading, message}){
        state.editGroupRequest = {
            loading: loading,
            message: message
        }
    },
    setDeleteGroupRequest(state, {loading, message}){
        state.deleteGroupRequest = {
            loading: loading,
            message: message
        }
    },
    markProductGroupsChanged(){
        groupsWrites += 1;
    },
    updateProductGroups(state, newList){
        groupsWrites += 1;
        state.productGroups = newList;
    },
}

export const productGroup = {
    namespaced: true,
    state,
    actions,
    mutations,
}
